import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Cookie } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = 'rs_consent';
export const POLICY_VERSION = '2026-06';

export const readConsent = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch { return null; }
};
export const openCookieSettings = () => window.dispatchEvent(new CustomEvent('restosupply:open-cookies'));

const CookieBanner = () => {
  const { t } = useTranslation();
  const { user, getAuthHeader } = useAuth();
  const [visible, setVisible] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const saved = readConsent();
    if (!saved || saved.policy_version !== POLICY_VERSION) setVisible(true);
    else setPrefs({ analytics: !!saved.analytics, marketing: !!saved.marketing });
    const h = () => setVisible(true);
    window.addEventListener('restosupply:open-cookies', h);
    return () => window.removeEventListener('restosupply:open-cookies', h);
  }, []);

  const save = async (p) => {
    const consent = { necessary: true, ...p, policy_version: POLICY_VERSION, updated_at: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(consent));
    if (user) axios.post(`${API}/consent`, consent, { headers: { Authorization: getAuthHeader() } }).catch(() => {});
    setPrefs(p);
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4" data-testid="cookie-banner">
      <div className="mx-auto max-w-3xl bg-white border border-slate-200 rounded-xl shadow-xl p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <Cookie size={22} className="text-emerald-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-bold text-slate-900">{t('cookies.title')}</p>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{t('cookies.text')}{' '}
              <a href="/privacidad" className="text-emerald-700 underline" data-testid="cookie-privacy-link">{t('cookies.privacy')}</a>{' · '}
              <a href="/cookies" className="text-emerald-700 underline" data-testid="cookie-policy-link">{t('cookies.cookiesPolicy')}</a>
            </p>
            <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-700">
              <label className="flex items-center gap-1.5"><input type="checkbox" checked disabled /> {t('cookies.necessary')}</label>
              <label className="flex items-center gap-1.5"><input type="checkbox" data-testid="cookie-analytics" checked={prefs.analytics} onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })} /> {t('cookies.analytics')}</label>
              <label className="flex items-center gap-1.5"><input type="checkbox" data-testid="cookie-marketing" checked={prefs.marketing} onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })} /> {t('cookies.marketing')}</label>
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          <Button variant="outline" size="sm" data-testid="cookie-reject-all" onClick={() => save({ analytics: false, marketing: false })}>{t('cookies.rejectAll')}</Button>
          <Button variant="outline" size="sm" data-testid="cookie-save-prefs" onClick={() => save(prefs)}>{t('cookies.savePrefs')}</Button>
          <Button size="sm" data-testid="cookie-accept-all" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => save({ analytics: true, marketing: true })}>{t('cookies.acceptAll')}</Button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
